import { normalizeRoomCode } from './protocol'

export interface RoomTarget {
  code: string
  signalUrl: string
  shareUrl: string
  hostUserId?: string
}

/** Old social servers send only a code; newer ones retain the actual host. */
export function parseSharedRoomTarget(
  room: { code: string; share_url?: string | null },
  defaultEndpoint: string,
  allowLan = false,
): RoomTarget {
  const target = parseRoomTarget(room.share_url || room.code, defaultEndpoint, allowLan)
  if (target.code !== normalizeRoomCode(room.code)) {
    throw new Error('The room link does not match the room code.')
  }
  return target
}

/** Never navigate the privileged WebView to the supplied host. */
export function parseRoomTarget(
  input: string,
  defaultEndpoint: string,
  allowLan = false,
): RoomTarget {
  const value = input.trim()
  let origin: URL
  let rawCode: string
  const direct = normalizeRoomCode(value)
  if (direct || value.startsWith('/r/')) {
    origin = new URL(defaultEndpoint)
    if (!['wss:', 'ws:'].includes(origin.protocol)) throw new Error('Invalid signaling endpoint.')
    origin.protocol = origin.protocol === 'wss:' ? 'https:' : 'http:'
    const path = direct ? null : /^\/r\/([^/?#]+)\/?$/.exec(value)
    if (!direct && !path) throw new Error('Use a room code or a link ending in /r/CODE.')
    try {
      rawCode = direct || decodeURIComponent(path![1])
    } catch {
      throw new Error('The room link contains an invalid escape sequence.')
    }
  } else {
    try {
      origin = new URL(value)
    } catch {
      throw new Error('Use a room code or a full HTTPS room link.')
    }
    if (!['https:', 'http:'].includes(origin.protocol) || origin.username || origin.password) {
      throw new Error('Room links must use HTTPS and cannot contain credentials.')
    }
    if (['tauri.localhost', 'asset.localhost'].includes(origin.hostname)) {
      throw new Error(
        'This link points to the desktop app, not a room server. Ask the host for their current share link.',
      )
    }
    if (origin.protocol === 'http:' && !allowLan) {
      throw new Error('HTTP rooms require verified LAN transport. Use an HTTPS link.')
    }
    const path = /^\/r\/([^/]+)\/?$/.exec(origin.pathname)
    if (!path) throw new Error('The room link must end in /r/CODE.')
    try {
      rawCode = decodeURIComponent(path[1])
    } catch {
      throw new Error('The room link contains an invalid escape sequence.')
    }
  }
  const code = normalizeRoomCode(rawCode)
  if (!code || /[/?#]/.test(rawCode)) throw new Error('Invalid room code. Example: K7M2QX.')
  const shareUrl = `${origin.origin}/r/${code}`
  const signal = new URL(
    direct || value.startsWith('/r/') ? defaultEndpoint : `${origin.origin}/ws`,
  )
  if (signal.protocol === 'https:') signal.protocol = 'wss:'
  if (signal.protocol === 'http:') signal.protocol = 'ws:'
  return { code, signalUrl: signal.href, shareUrl }
}
